import { FC, HTMLProps } from 'react';
import clsx from 'clsx';
import styles from './PromptModal.module.scss';

import { Modal } from 'components/Modal';
import { Button, ButtonVariant } from '@clover/web-lib-consumer-ui';
import { Icon } from 'components/Icon';
import { CloseButton } from 'components/CloseButton';
import { generateId } from 'util/generateId';
import { useRefHandleFocus } from 'hooks/useHandleFocus';

export type PromptModalProps = HTMLProps<HTMLElement> & {
  isOpen: boolean;
  className?: string;
  primaryButtonContent?: string;
  secondaryButtonContent?: string;
  iconName?: string;
  title?: string;
  subtitle?: string;
  primaryOnClick?: () => void;
  secondaryOnClick?: () => void;
  onClose: () => void;
};

export const PromptModal: FC<PromptModalProps> = ({ isOpen, className, primaryOnClick, secondaryOnClick, onClose, iconName, title, subtitle, primaryButtonContent, secondaryButtonContent, children, ...props }) => {
  const showPrimary = primaryButtonContent && primaryOnClick;
  const showSecondary = secondaryButtonContent && secondaryOnClick;
  const labelId = generateId();
  const PRIMARY_TEST_ID = 'prompt-modal-primary-button';
  const SECONDARY_TEST_ID = 'prompt-modal-secondary-button';

  const [ref] = useRefHandleFocus(isOpen);

  return (
    <Modal
      aria={{ labelledby: labelId }}
      isOpen = {isOpen}
      className={clsx(styles.modal, className)}
      onClose={onClose}
    >
      <header className={styles.modalHeader}>
        <CloseButton className={styles.closeButton} onClick={onClose} />
      </header>
      <section className={styles.body}>
        {iconName && <Icon className={styles.icon} name={iconName} height={64} width={64}></Icon>}
        <p className={styles.title} id={labelId} tabIndex={-1}>{title}</p>
        {subtitle && <div className={styles.subtitle}>
          <div>{subtitle}</div>
        </div>}
        {children}
      </section>
      <footer className={styles.footer}>
        {showPrimary && <Button
          className={styles.button}
          onClick={primaryOnClick}
          data-testid={PRIMARY_TEST_ID}
          ref={ref}>
            {primaryButtonContent}
          </Button>}
        {showSecondary && <Button
          variant={ButtonVariant.INVERTED}
          className={styles.button}
          onClick={secondaryOnClick}
          data-testid={SECONDARY_TEST_ID}>
            {secondaryButtonContent}
          </Button>}
      </footer>
    </Modal>
  );
};
